import React, { useEffect, useMemo, useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import ChatCard from './ChatCard';
import JobPostingStepsLog, { getJobPostingStepProgressSummary } from './JobPostingStepsLog';
import { ProcessLogsModal, ProcessStatusCard, getLatestRunState, getProcessInsight } from './ProcessLogSummary';
import { aiAutomationApi } from '../utils/aiAutomationApi';
import { getCid } from '../utils/encryptedStorage';
import {
  findWorkflowSnapshot,
  workflowStateToVisibleStatus,
  workflowSyntheticLog,
} from '../utils/workflowSnapshot';
import {
  formatJobPostingErrorMessage,
  isGenericJobPostingErrorMessage,
} from '../utils/jobPostingErrors';
import '../styles/chat.css';

/**
 * Convert file-log entries (from the backend JSONL) to the frontend log-entry
 * format that ProcessLogsModal / JobPostingStepsLog can consume.
 */
const fileLogsToEntries = (fileLogs) => {
  if (!Array.isArray(fileLogs)) return [];
  return fileLogs.map((entry) => ({
    message: entry.message || '',
    logMessage: entry.message || '',
    logType: entry.log_type === 'error' ? 'error' : entry.log_type === 'success' ? 'success' : entry.log_type === 'warning' ? 'warning' : 'info',
    type: entry.log_type === 'error' ? 'error' : 'info',
    status: entry.log_type === 'error' ? 'error' : entry.log_type === 'success' ? 'completed' : 'in_progress',
    timestamp: entry.timestamp || '',
    platform: entry.platform || '',
    phase: entry.phase || '',
  }));
};

const JobPostingWaitNotice = ({ isDark }) => (
  <div
    style={{
      marginTop: 12,
      borderRadius: 14,
      border: `1px solid ${isDark ? 'rgba(251, 191, 36, 0.35)' : 'rgba(251, 191, 36, 0.65)'}`,
      background: isDark ? 'rgba(120, 53, 15, 0.22)' : 'rgba(255, 251, 235, 0.96)',
      color: isDark ? '#FDE68A' : '#92400E',
      padding: '10px 12px',
      fontSize: 12,
      lineHeight: 1.5,
      fontWeight: 600,
    }}
  >
    Please stay on this page. After confirming the job posting, the automation may take around 10 minutes to complete.
  </div>
);

/**
 * Renders the full session-file log section inside a ProcessLogsModal.
 * These logs come from the backend file: session_logs/jobposting/<session_id>/<platform>.jsonl
 */
const FullFileLogSection = ({ fileState, isDark, onRefetch }) => {
  if (!fileState) return null;
  const { loading, logs, error, fetched } = fileState;

  const logTypeColor = (logType) => {
    if (logType === 'error') return isDark ? '#FCA5A5' : '#DC2626';
    if (logType === 'success') return isDark ? '#6EE7B7' : '#16A34A';
    if (logType === 'warning') return isDark ? '#FDE68A' : '#CA8A04';
    return isDark ? '#94A3B8' : '#475569';
  };

  return (
    <div
      style={{
        marginTop: 20,
        borderTop: `1px solid ${isDark ? 'rgba(71,85,105,0.5)' : 'rgba(226,232,240,1)'}`,
        paddingTop: 14,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 10,
        }}
      >
        <span
          style={{
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            color: isDark ? '#94A3B8' : '#64748B',
          }}
        >
          Full Session Log (File)
        </span>
        <button
          type="button"
          onClick={onRefetch}
          disabled={loading}
          style={{
            fontSize: 11,
            fontWeight: 700,
            padding: '4px 10px',
            borderRadius: 999,
            border: `1px solid ${isDark ? 'rgba(71,85,105,0.7)' : 'rgba(203,213,225,1)'}`,
            background: 'transparent',
            color: isDark ? '#CBD5E1' : '#475569',
            cursor: loading ? 'default' : 'pointer',
            opacity: loading ? 0.6 : 1,
          }}
        >
          {loading ? 'Loading…' : '↺ Refresh'}
        </button>
      </div>

      {loading && (
        <div style={{ padding: '12px 0', color: isDark ? '#94A3B8' : '#64748B', fontSize: 13 }}>
          Loading full logs…
        </div>
      )}

      {!loading && error && (
        <div style={{ padding: '12px 0', color: isDark ? '#FCA5A5' : '#DC2626', fontSize: 13 }}>
          {error}
        </div>
      )}

      {!loading && !error && fetched && logs.length === 0 && (
        <div style={{ padding: '12px 0', color: isDark ? '#94A3B8' : '#64748B', fontSize: 13 }}>
          No file logs found for this session yet.
        </div>
      )}

      {!loading && !error && logs.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {logs.map((entry, idx) => (
            <div
              key={idx}
              style={{
                display: 'flex',
                gap: 10,
                alignItems: 'flex-start',
                padding: '5px 8px',
                borderRadius: 8,
                background: isDark ? 'rgba(30,41,59,0.5)' : 'rgba(248,250,252,0.9)',
                borderLeft: `3px solid ${logTypeColor(entry.log_type)}`,
              }}
            >
              <span
                style={{
                  fontSize: 10,
                  color: isDark ? '#64748B' : '#94A3B8',
                  whiteSpace: 'nowrap',
                  marginTop: 2,
                  minWidth: 70,
                }}
              >
                {entry.timestamp
                  ? new Date(entry.timestamp).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
                  : '—'}
              </span>
              <span style={{ fontSize: 12, color: isDark ? '#CBD5E1' : '#334155', flex: 1, wordBreak: 'break-word' }}>
                {entry.message}
              </span>
              {entry.log_type && entry.log_type !== 'info' && (
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: logTypeColor(entry.log_type),
                    whiteSpace: 'nowrap',
                    textTransform: 'uppercase',
                  }}
                >
                  {entry.log_type}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const PlatformIcon = ({ label }) => {
  if (label === 'LinkedIn') {
    return <span style={{ fontSize: 15, fontWeight: 800 }}>in</span>;
  }

  return <span style={{ fontSize: 15, fontWeight: 800 }}>N</span>;
};

const getScreenshotBaseUrls = () => {
  const urls = [];
  const addUrl = (url) => {
    if (!url) return;
    const normalized = url.replace(/\/$/, '');
    if (!urls.includes(normalized)) {
      urls.push(normalized);
    }
  };

  if (typeof window !== 'undefined' && window.location?.origin) {
    const origin = window.location.origin.replace(/\/$/, '');
    addUrl(`${origin}/api/v1/screenshots`);
    addUrl(`${origin}/aibot/api/v1/screenshots`);
    addUrl(`${origin}/screenshots`);
    addUrl(`${origin}/aibot/screenshots`);
  }

  const apiUrl = process.env.REACT_APP_AI_AUTOMATION_URL;
  if (apiUrl) {
    const normalizedApiUrl = apiUrl.replace(/\/$/, '');
    addUrl(`${normalizedApiUrl}/screenshots`);
    addUrl(`${normalizedApiUrl.replace(/\/api\/v1$/i, '')}/screenshots`);
  }

  const baseUrl = process.env.REACT_APP_AI_AUTOMATION_BASE_URL;
  if (baseUrl) {
    const normalizedBaseUrl = baseUrl.replace(/\/$/, '');
    addUrl(`${normalizedBaseUrl}/api/v1/screenshots`);
    addUrl(`${normalizedBaseUrl}/screenshots`);
  }

  if (urls.length === 0) {
    addUrl('http://localhost:8100/api/v1/screenshots');
    addUrl('http://localhost:8100/screenshots');
  }

  return urls;
};

const ScreenshotImage = ({ artifact, alt, isDark }) => {
  const urls = useMemo(
    () => artifact?.imageUrls || (artifact?.imageUrl ? [artifact.imageUrl] : []),
    [artifact?.imageUrls, artifact?.imageUrl],
  );
  const [currentUrl, setCurrentUrl] = useState(urls[0] || '');

  useEffect(() => {
    setCurrentUrl(urls[0] || '');
  }, [artifact?.filename, urls]);

  if (!currentUrl) {
    return (
      <div
        style={{
          width: '100%',
          height: 220,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 16,
          border: `1px solid ${isDark ? 'rgba(71, 85, 105, 0.6)' : 'rgba(226, 232, 240, 0.95)'}`,
          background: isDark ? '#0F172A' : '#F8FAFC',
          color: isDark ? '#CBD5E1' : '#64748B',
          fontSize: 13,
          fontWeight: 600,
          textAlign: 'center',
          padding: 16,
        }}
      >
        Screenshot preview is not available from the current server path.
      </div>
    );
  }

  return (
    <img
      src={currentUrl}
      alt={alt}
      onError={() => {
        const currentIndex = urls.indexOf(currentUrl);
        const nextUrl = currentIndex >= 0 ? urls[currentIndex + 1] : '';
        setCurrentUrl(nextUrl || '');
      }}
      style={{
        width: '100%',
        height: 220,
        objectFit: 'cover',
        borderRadius: 16,
        border: `1px solid ${isDark ? 'rgba(71, 85, 105, 0.6)' : 'rgba(226, 232, 240, 0.95)'}`,
        background: isDark ? '#0F172A' : '#F8FAFC',
      }}
    />
  );
};

const extractLatestArtifact = (logs = []) => {
  if (!Array.isArray(logs) || logs.length === 0) return null;

  const screenshotBaseUrls = getScreenshotBaseUrls();
  const latestRunEntries = getLatestRunState(logs).windowEntries;
  const preferredLogs = latestRunEntries.length > 0
    ? latestRunEntries.map((entry) => entry.raw || entry)
    : logs;
  const sourceSets = [preferredLogs, logs].filter((set, index, all) => (
    Array.isArray(set) &&
    set.length > 0 &&
    all.findIndex((candidate) => candidate === set) === index
  ));

  const findDetailedFailureMessage = (entries, centerIndex) => {
    const nearbyMessages = entries
      .slice(Math.max(0, centerIndex - 12), Math.min(entries.length, centerIndex + 13))
      .map((log) => String(log?.message || '').trim())
      .filter(Boolean);
    const genericFailures = new Set([
      'failed to post job on naukri',
      'failed to post job on linkedin',
      'naukri: failed to post job on naukri',
      'linkedin: failed to post job on linkedin',
      'naukri job posting failed',
      'linkedin job posting failed',
      'job posting failed',
      'failed to post job',
    ]);
    return [...nearbyMessages].reverse().find((entry) => {
      const normalized = entry
        .replace(/^[^\w]+/, '')
        .replace(/^\w+\s+job\s+posting\s+failed:\s*/i, '')
        .trim()
        .toLowerCase()
        .replace(/\s+/g, ' ');
      return (
        /failed|could not|unable|issue|error/i.test(entry) &&
        !genericFailures.has(normalized)
      );
    }) || null;
  };

  for (const sourceLogs of sourceSets) {
    for (let index = sourceLogs.length - 1; index >= 0; index -= 1) {
      const message = String(sourceLogs[index]?.message || '').trim();
      const screenshotMatch =
        message.match(/(?:Error\s+)?Screenshot saved:\s*(?:.*\/)?([^\s/]+\.(?:png|jpg|jpeg))/i) ||
        message.match(/Screenshot saved before browser close:\s*(?:.*\/)?([^\s/]+\.(?:png|jpg|jpeg))/i) ||
        message.match(/Screenshot:\s*(?:.*\/)?([^\s/]+\.(?:png|jpg|jpeg))/i);
      if (!screenshotMatch) continue;

      const filename = screenshotMatch[1];
      const nearbyMessages = sourceLogs
        .slice(Math.max(0, index - 6), Math.min(sourceLogs.length, index + 7))
        .map((log) => String(log?.message || '').trim());

      const pageUrlMessage = nearbyMessages.find((entry) => entry.toLowerCase().includes('page url:'));
      const pageTitleMessage = nearbyMessages.find((entry) => entry.toLowerCase().includes('page title:'));
      const failureMessage = findDetailedFailureMessage(sourceLogs, index) || [...nearbyMessages].reverse().find((entry) => entry.toLowerCase().includes('failed'));
      const successMessage = nearbyMessages.find((entry) => entry.toLowerCase().includes('posted successfully'));
      const lowerFilename = filename.toLowerCase();
      const lowerMessage = message.toLowerCase();

      let kind = 'neutral';
      if (
        lowerFilename.includes('success') ||
        lowerFilename.includes('posted') ||
        lowerFilename.includes('completed') ||
        successMessage
      ) {
        kind = 'success';
      } else if (
        lowerFilename.includes('failed') ||
        lowerFilename.includes('failure') ||
        lowerFilename.includes('error') ||
        lowerFilename.includes('not_found') ||
        lowerFilename.includes('timed_out') ||
        lowerFilename.includes('timeout') ||
        lowerFilename.includes('blocked') ||
        lowerFilename.includes('payment') ||
        lowerMessage.includes('error screenshot saved') ||
        failureMessage
      ) {
        kind = 'failure';
      }

      return {
        kind,
        filename,
        imageUrl: `${screenshotBaseUrls[0]}/${filename}`,
        imageUrls: screenshotBaseUrls.map((baseUrl) => `${baseUrl}/${filename}`),
        pageUrl: pageUrlMessage ? pageUrlMessage.replace(/^.*Page URL:\s*/i, '') : null,
        pageTitle: pageTitleMessage ? pageTitleMessage.replace(/^.*Page title:\s*/i, '') : null,
        failureMessage: failureMessage || null,
        successMessage: successMessage || null,
      };
    }
  }

  return null;
};

const extractDetailedFailureFromLogs = (logs = []) => {
  if (!Array.isArray(logs) || logs.length === 0) return null;
  const latestRunEntries = getLatestRunState(logs).windowEntries;
  const sourceLogs = latestRunEntries.length > 0
    ? latestRunEntries.map((entry) => entry.raw || entry)
    : logs;
  const genericFailures = new Set([
    'failed to post job on naukri',
    'failed to post job on linkedin',
    'naukri: failed to post job on naukri',
    'linkedin: failed to post job on linkedin',
    'naukri job posting failed',
    'linkedin job posting failed',
    'job posting failed',
    'failed to post job',
  ]);
  for (let index = sourceLogs.length - 1; index >= 0; index -= 1) {
    const message = String(sourceLogs[index]?.message || '').trim();
    if (!message || !/failed|could not|unable|issue|error/i.test(message)) continue;
    const cleaned = message
      .replace(/^[^\w]+/, '')
      .replace(/^\w+\s+job\s+posting\s+failed:\s*/i, '')
      .trim();
    const normalized = cleaned.toLowerCase().replace(/\s+/g, ' ');
    if (!genericFailures.has(normalized)) return cleaned;
  }
  return null;
};

const resolveArtifactKind = (artifact, channelStatus) => {
  if (!artifact) return 'neutral';

  const lowerFilename = String(artifact.filename || '').toLowerCase();
  if (lowerFilename.includes('success')) return 'success';
  if (lowerFilename.includes('failed') || lowerFilename.includes('failure') || lowerFilename.includes('error')) return 'failure';
  if (artifact.kind === 'success' || artifact.kind === 'failure') return artifact.kind;
  if (channelStatus === 'success') return 'success';
  if (channelStatus === 'failed') return 'failure';
  return 'neutral';
};

const artifactFromBackendResult = (result) => {
  if (!result || typeof result !== 'object') return null;
  const filename = String(result.screenshot_filename || result.screenshot_path || '').trim();
  if (!filename) return null;

  const screenshotBaseUrls = getScreenshotBaseUrls();
  const status = String(result.status || '').toLowerCase();
  const lowerFilename = filename.toLowerCase();
  let kind = 'neutral';

  if (
    lowerFilename.includes('success') ||
    lowerFilename.includes('posted') ||
    lowerFilename.includes('completed')
  ) {
    kind = 'success';
  } else if (
    lowerFilename.includes('failed') ||
    lowerFilename.includes('failure') ||
    lowerFilename.includes('error') ||
    lowerFilename.includes('not_found') ||
    lowerFilename.includes('timed_out') ||
    lowerFilename.includes('timeout') ||
    lowerFilename.includes('blocked') ||
    lowerFilename.includes('payment')
  ) {
    kind = 'failure';
  } else if (status === 'posted') {
    kind = 'success';
  } else if (status === 'failed') {
    kind = 'failure';
  }

  return {
    kind,
    filename,
    imageUrl: `${screenshotBaseUrls[0]}/${filename}`,
    imageUrls: screenshotBaseUrls.map((baseUrl) => `${baseUrl}/${filename}`),
    pageUrl: result.page_url || null,
    pageTitle: result.page_title || null,
    failureMessage: kind === 'failure'
      ? result.failure_reason || result.error_details || result.error || result.message || null
      : null,
    successMessage: kind === 'success' ? result.message || null : null,
    startedAt: result.started_at || null,
    completedAt: result.completed_at || null,
  };
};

const artifactFromWorkflowSnapshot = (row) => {
  const evidence = row?.evidence && typeof row.evidence === 'object' ? row.evidence : {};
  if (!evidence.screenshot_path) return null;

  return artifactFromBackendResult({
    screenshot_path: evidence.screenshot_path,
    page_url: evidence.page_url,
    page_title: evidence.page_title,
    status: row?.state === 'succeeded' ? 'posted' : row?.state === 'failed' ? 'failed' : row?.state,
    error: row?.reason,
    message: row?.status_label || row?.reason,
    completed_at: row?.updated_at,
  });
};

const getChannelTrackerFailureReason = (cardKey, channelProcessStatus) =>
  cardKey === 'linkedin'
    ? channelProcessStatus?.reason_failed_job_post_linkedin
    : channelProcessStatus?.reason_failed_job_post_naukri;

const getLatestLogTimestamp = (logs = []) => {
  if (!Array.isArray(logs) || logs.length === 0) return 0;
  return logs.reduce((latest, log) => {
    const timestamp = log?.timestamp || log?.created_at || log?.rawTimestamp;
    const value = timestamp ? new Date(timestamp).getTime() : 0;
    return Number.isFinite(value) && value > latest ? value : latest;
  }, 0);
};

const isExplicitPostingSuccessLog = (log = {}) => {
  const message = String(log?.message || log?.logMessage || '').toLowerCase();
  const status = String(log?.status || log?.logType || log?.type || '').toLowerCase();

  return (
    message.includes('posted successfully') ||
    message.includes('job posted successfully') ||
    message.includes('successfully published') ||
    (status === 'success' && message.includes('job'))
  );
};

const getLogsThroughLastExplicitSuccess = (logs = []) => {
  if (!Array.isArray(logs) || logs.length === 0) return logs;

  for (let index = logs.length - 1; index >= 0; index -= 1) {
    if (isExplicitPostingSuccessLog(logs[index])) {
      return logs.slice(0, index + 1);
    }
  }

  return logs;
};

const getFailedRunLogs = (logs = []) => {
  if (!Array.isArray(logs) || logs.length === 0) return logs;

  const latestRunEntries = getLatestRunState(logs).windowEntries;
  const latestRunLogs = latestRunEntries.length > 0
    ? latestRunEntries.map((entry) => entry.raw || entry)
    : logs;

  const latestRunHasFailure = latestRunEntries.some((entry) => {
    const message = String(entry?.raw?.message || '').toLowerCase();
    return (
      message.includes('failed to post job') ||
      message.includes('job posting failed') ||
      message.includes('could not be completed') ||
      message.includes('could not find the publish controls') ||
      message.includes('try reposting')
    );
  });

  if (!latestRunHasFailure) return latestRunLogs;

  return latestRunLogs.filter((log) => !isExplicitPostingSuccessLog(log));
};

const hasLatestRunExplicitPostingSuccess = (logs = []) =>
  getLatestRunState(logs).windowEntries.some((entry) => isExplicitPostingSuccessLog(entry.raw));

const getLatestRunExplicitSuccessEntry = (logs = []) =>
  [...getLatestRunState(logs).windowEntries]
    .reverse()
    .find((entry) => isExplicitPostingSuccessLog(entry.raw)) || null;

const MAINTENANCE_ONLY_PATTERNS = [
  'starting new browser instance',
  'browser mode:',
  'warning: naukri browser is set to headless mode',
  'warning: linkedin browser is set to headless mode',
  'continuing without proxy',
  'found saved naukri session - loading',
  'found saved linkedin session - loading',
  'loaded saved session successfully',
  'browser started successfully',
  'starting naukri login process',
  'starting linkedin login process',
  'navigating to naukri recruit login page',
  'navigating to linkedin',
  'navigated to naukri recruit login page',
  'url: https://recruit.naukri.com/',
  'page title: my naukri for recruiters',
  'found visible logged-in indicator',
  'found visible indicator: a:has-text("logout")',
  'already logged in via saved session',
  'saving naukri session',
  'saving linkedin session',
  'session saved successfully',
  'saved ',
  'cookies and',
  'storage origins',
];

const isMaintenanceOnlyRun = (windowEntries = []) => {
  const meaningfulEntries = (windowEntries || []).filter((entry) => {
    const content = String(entry?.message || '').trim().toLowerCase();
    if (!content) return false;
    if (content.includes('job posted successfully') || content.includes('posted successfully')) return false;
    return !(
      content.includes('screenshot saved') ||
      content.includes('page url:') ||
      content.includes('page title:') ||
      content.includes('browser context closed') ||
      content.includes('playwright stopped')
    );
  });

  return (
    meaningfulEntries.length > 0 &&
    meaningfulEntries.every((entry) => {
      const content = String(entry?.message || '').toLowerCase();
      return MAINTENANCE_ONLY_PATTERNS.some((pattern) => content.includes(pattern));
    })
  );
};

const SUCCESS_CONTEXT_ONLY_PATTERNS = [
  'screenshot saved',
  'page url:',
  'page title:',
  'browser context closed',
  'browser closed',
  'playwright stopped',
  'saving naukri session',
  'saving linkedin session',
  'session saved successfully',
  'saved ',
  'cookies and',
  'storage origins',
];

const isSuccessContextOnlyEntry = (entry) => {
  const content = String(entry?.message || '').toLowerCase();
  return SUCCESS_CONTEXT_ONLY_PATTERNS.some((pattern) => content.includes(pattern));
};

const JobPostingLogs = ({
  sessionId = '',
  linkedinLogs = [],
  naukriLogs = [],
  linkedinProcessing = false,
  naukriProcessing = false,
  jobPostingActive = false,
  selectedChannels = [],
  availableChannels = [],
  postedChannels = [],
  onRequestPostJob,
  onRetryChannel,
}) => {
  const { isDark } = useTheme();
  const [activeLogModal, setActiveLogModal] = useState(null);
  const [activeImagePreview, setActiveImagePreview] = useState(null);
  const [activeImagePreviewUrl, setActiveImagePreviewUrl] = useState('');
  const [relativeTimeNow, setRelativeTimeNow] = useState(() => Date.now());
  const [backendJobPosting, setBackendJobPosting] = useState(null);
  const [workflowSnapshot, setWorkflowSnapshot] = useState(null);
  // Per-channel status from hr_talent_gpt_process_tracking via /process-status API
  const [channelProcessStatus, setChannelProcessStatus] = useState(null);
  // Full file-based logs for the modal: { linkedin: {loading,logs,error}, naukri: {loading,logs,error} }
  const [fileLogsState, setFileLogsState] = useState({
    linkedin: { loading: false, logs: [], error: null, fetched: false },
    naukri: { loading: false, logs: [], error: null, fetched: false },
  });

  useEffect(() => {
    setActiveImagePreviewUrl(activeImagePreview?.imageUrl || activeImagePreview?.imageUrls?.[0] || '');
  }, [activeImagePreview]);

  useEffect(() => {
    let cancelled = false;

    const loadBackendJobPosting = async () => {
      if (!sessionId) {
        if (!cancelled) setBackendJobPosting(null);
        return;
      }

      try {
        const data = await aiAutomationApi.get(`/job-postings/by-session/${sessionId}`);
        if (!cancelled) {
          setBackendJobPosting(data?.job_posting || null);
        }
      } catch {
        if (!cancelled) {
          setBackendJobPosting(null);
        }
      }
    };

    const loadChannelProcessStatus = async () => {
      if (!sessionId) {
        if (!cancelled) setChannelProcessStatus(null);
        return;
      }
      try {
        const data = await aiAutomationApi.get(`/process-status/${sessionId}`);
        if (!cancelled) setChannelProcessStatus(data || null);
      } catch {
        if (!cancelled) setChannelProcessStatus(null);
      }
    };

    const loadWorkflowSnapshot = async () => {
      const cid = getCid();
      if (!sessionId || !cid) {
        if (!cancelled) setWorkflowSnapshot(null);
        return;
      }

      try {
        const data = await aiAutomationApi.getWorkflowSnapshot(sessionId, cid);
        if (!cancelled) setWorkflowSnapshot(data || null);
      } catch {
        if (!cancelled) setWorkflowSnapshot(null);
      }
    };

    loadBackendJobPosting();
    loadChannelProcessStatus();
    loadWorkflowSnapshot();
    return () => {
      cancelled = true;
    };
  }, [sessionId, linkedinLogs, naukriLogs, linkedinProcessing, naukriProcessing]);

  // When a new run starts (processing becomes true), reset cached file logs so modal re-fetches fresh data
  useEffect(() => {
    if (linkedinProcessing || naukriProcessing) {
      setFileLogsState({
        linkedin: { loading: false, logs: [], error: null, fetched: false },
        naukri: { loading: false, logs: [], error: null, fetched: false },
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedinProcessing, naukriProcessing]);

  // Fetch file-based full logs when the modal is opened
  useEffect(() => {
    if (!activeLogModal || !sessionId) return;
    const platform = activeLogModal; // 'linkedin' | 'naukri'
    // Always re-fetch when modal opens (don't cache across runs)

    setFileLogsState((prev) => ({
      ...prev,
      [platform]: { loading: true, logs: [], error: null, fetched: false },
    }));

    aiAutomationApi
      .get(`/logs/session/${sessionId}/jobposting/${platform}`)
      .then((data) => {
        setFileLogsState((prev) => ({
          ...prev,
          [platform]: { loading: false, logs: data?.logs || [], error: null, fetched: true },
        }));
      })
      .catch(() => {
        setFileLogsState((prev) => ({
          ...prev,
          [platform]: { loading: false, logs: [], error: 'Could not load full log file.', fetched: true },
        }));
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeLogModal, sessionId]);

  const getWorkflowRow = (channel) => findWorkflowSnapshot(workflowSnapshot, 'job_posting', channel);
  const linkedinWorkflowRow = getWorkflowRow('linkedin');
  const naukriWorkflowRow = getWorkflowRow('naukri');
  const linkedinWorkflowStatus = workflowStateToVisibleStatus(linkedinWorkflowRow);
  const naukriWorkflowStatus = workflowStateToVisibleStatus(naukriWorkflowRow);
  const getEffectiveLogs = (channel, logs) => {
    if (Array.isArray(logs) && logs.length > 0) return logs;
    const row = getWorkflowRow(channel);
    const syntheticLog = workflowSyntheticLog(row, `${channel === 'linkedin' ? 'LinkedIn' : 'Naukri'} posting status updated.`);
    return syntheticLog ? [syntheticLog] : [];
  };
  const effectiveLinkedinLogs = getEffectiveLogs('linkedin', linkedinLogs);
  const effectiveNaukriLogs = getEffectiveLogs('naukri', naukriLogs);
  const hasLinkedinActivity = effectiveLinkedinLogs.length > 0 || linkedinProcessing || (linkedinWorkflowStatus && linkedinWorkflowStatus !== 'not_started');
  const hasNaukriActivity = effectiveNaukriLogs.length > 0 || naukriProcessing || (naukriWorkflowStatus && naukriWorkflowStatus !== 'not_started');
  const normalizedAvailableChannels = (availableChannels || []).map((channel) => String(channel || '').toLowerCase());
  const normalizedBackendPostedChannels = (
    Array.isArray(backendJobPosting?.posted_channels) ? backendJobPosting.posted_channels : []
  ).map((channel) => String(channel || '').toLowerCase());
  const normalizedPostedChannels = Array.from(new Set([
    ...(postedChannels || []).map((channel) => String(channel || '').toLowerCase()),
    ...normalizedBackendPostedChannels,
  ]));
  const backendChannelStatuses = backendJobPosting?.channel_statuses || {};
  const backendChannelResults = backendJobPosting?.channel_results || {};
  const selectedChannelKeys = (selectedChannels || []).map((channel) => String(channel || '').toLowerCase());
  const hasCurrentRunContext =
    selectedChannelKeys.length > 0 ||
    linkedinProcessing ||
    naukriProcessing ||
    jobPostingActive;

  const hasExplicitLinkedinContext =
    selectedChannelKeys.includes('linkedin') ||
    linkedinProcessing ||
    (linkedinWorkflowStatus && linkedinWorkflowStatus !== 'not_started') ||
    normalizedAvailableChannels.includes('linkedin') ||
    normalizedPostedChannels.includes('linkedin');
  const hasExplicitNaukriContext =
    selectedChannelKeys.includes('naukri') ||
    naukriProcessing ||
    (naukriWorkflowStatus && naukriWorkflowStatus !== 'not_started') ||
    normalizedAvailableChannels.includes('naukri') ||
    normalizedPostedChannels.includes('naukri');
  const latestLinkedinTimestamp = getLatestLogTimestamp(linkedinLogs);
  const latestNaukriTimestamp = getLatestLogTimestamp(naukriLogs);
  const hideStaleLinkedinHistory =
    !hasExplicitLinkedinContext &&
    hasExplicitNaukriContext &&
    latestLinkedinTimestamp > 0 &&
    latestNaukriTimestamp > latestLinkedinTimestamp;
  const hideStaleNaukriHistory =
    !hasExplicitNaukriContext &&
    hasExplicitLinkedinContext &&
    latestNaukriTimestamp > 0 &&
    latestLinkedinTimestamp > latestNaukriTimestamp;

  const showLinkedin =
    hasCurrentRunContext
      ? selectedChannelKeys.includes('linkedin') ||
        linkedinProcessing ||
        (linkedinWorkflowStatus && linkedinWorkflowStatus !== 'not_started') ||
        normalizedAvailableChannels.includes('linkedin') ||
        normalizedPostedChannels.includes('linkedin') ||
        (hasLinkedinActivity && !hideStaleLinkedinHistory)
      : normalizedAvailableChannels.includes('linkedin') ||
        (linkedinWorkflowStatus && linkedinWorkflowStatus !== 'not_started') ||
        normalizedPostedChannels.includes('linkedin') ||
        (hasLinkedinActivity && !hideStaleLinkedinHistory);
  const showNaukri =
    hasCurrentRunContext
      ? selectedChannelKeys.includes('naukri') ||
        naukriProcessing ||
        (naukriWorkflowStatus && naukriWorkflowStatus !== 'not_started') ||
        normalizedAvailableChannels.includes('naukri') ||
        normalizedPostedChannels.includes('naukri') ||
        (hasNaukriActivity && !hideStaleNaukriHistory)
      : normalizedAvailableChannels.includes('naukri') ||
        (naukriWorkflowStatus && naukriWorkflowStatus !== 'not_started') ||
        normalizedPostedChannels.includes('naukri') ||
        (hasNaukriActivity && !hideStaleNaukriHistory);

  const processCards = [
    showLinkedin
      ? {
          key: 'linkedin',
          title: 'LinkedIn Posting',
          workflowRow: linkedinWorkflowRow,
          metric: linkedinProcessing || linkedinWorkflowStatus === 'processing'
            ? 'Publishing to LinkedIn'
            : linkedinWorkflowRow?.status_label
            ? linkedinWorkflowRow.status_label
            : normalizedAvailableChannels.includes('linkedin') && linkedinLogs.length === 0
            ? 'Ready to publish to LinkedIn'
            : `${effectiveLinkedinLogs.length} update${effectiveLinkedinLogs.length === 1 ? '' : 's'}`,
          logs: effectiveLinkedinLogs,
          processing: linkedinProcessing || linkedinWorkflowStatus === 'processing',
        }
      : null,
    showNaukri
      ? {
          key: 'naukri',
          title: 'Naukri Posting',
          workflowRow: naukriWorkflowRow,
          metric: naukriProcessing || naukriWorkflowStatus === 'processing'
            ? 'Publishing to Naukri'
            : naukriWorkflowRow?.status_label
            ? naukriWorkflowRow.status_label
            : normalizedAvailableChannels.includes('naukri') && naukriLogs.length === 0
            ? 'Ready to publish to Naukri'
            : `${effectiveNaukriLogs.length} update${effectiveNaukriLogs.length === 1 ? '' : 's'}`,
          logs: effectiveNaukriLogs,
          processing: naukriProcessing || naukriWorkflowStatus === 'processing',
        }
      : null,
  ].filter(Boolean);
  useEffect(() => {
    if (processCards.length === 0) return undefined;

    const interval = setInterval(() => {
      setRelativeTimeNow(Date.now());
    }, 60000);

    return () => clearInterval(interval);
  }, [processCards.length]);
  const postedChannelLabels = (postedChannels || [])
    .map((channel) => String(channel || '').trim())
    .filter(Boolean);
  const latestArtifacts = useMemo(
    () => ({
      linkedin: artifactFromWorkflowSnapshot(linkedinWorkflowRow) || artifactFromBackendResult(backendChannelResults.linkedin) || extractLatestArtifact(effectiveLinkedinLogs),
      naukri: artifactFromWorkflowSnapshot(naukriWorkflowRow) || artifactFromBackendResult(backendChannelResults.naukri) || extractLatestArtifact(effectiveNaukriLogs),
    }),
    [backendChannelResults.linkedin, backendChannelResults.naukri, linkedinWorkflowRow, naukriWorkflowRow, effectiveLinkedinLogs, effectiveNaukriLogs],
  );
  const getChannelStatus = (card) => {
    const backendChannelResult = backendChannelResults?.[card.key] || null;
    const persistedStatus = String(backendChannelResult?.status || '').toLowerCase();
    const backendChannelStatus = String(backendChannelStatuses?.[card.key] || '').toLowerCase();
    const perChannelDbStatus = card.key === 'linkedin'
      ? (channelProcessStatus?.job_post_linkedin || 'not_started')
      : (channelProcessStatus?.job_post_naukri || 'not_started');

    // Authoritative per-session DB channel state wins before workflow/log fallbacks.
    // This prevents refresh races where workflow snapshots momentarily report idle and
    // the card flips from "Publishing" to "This channel has not started yet".
    if (perChannelDbStatus === 'processing') return 'processing';
    if (perChannelDbStatus === 'complete') return 'success';
    if (perChannelDbStatus === 'failed') return 'failed';
    if (card.processing) return 'processing';

    const workflowStatus = workflowStateToVisibleStatus(card.workflowRow);
    if (workflowStatus === 'processing') return 'processing';
    if (workflowStatus === 'success') return 'success';
    if (workflowStatus === 'failed') return 'failed';

    if (channelProcessStatus !== null && perChannelDbStatus === 'not_started') return 'not_started';
    if (card.workflowRow && workflowStatus === 'not_started') return 'not_started';

    // DB is source of truth: if DB explicitly confirms failure, never override with log analysis
    const dbExplicitFailed = persistedStatus === 'failed' || backendChannelStatus === 'failed' ||
      perChannelDbStatus === 'failed';
    const latestRunState = getLatestRunState(card.logs);
    const latestRunExplicitSuccess = hasLatestRunExplicitPostingSuccess(card.logs);
    const latestRunExplicitSuccessEntry = getLatestRunExplicitSuccessEntry(card.logs);
    const latestRunMaintenanceOnly = isMaintenanceOnlyRun(latestRunState.windowEntries);
    const hasAnyExplicitPostingSuccess = card.logs.some((log) => isExplicitPostingSuccessLog(log));
    const rawStepProgress = getJobPostingStepProgressSummary({
      logs: card.logs,
      platform: card.key === 'linkedin' ? 'LinkedIn' : 'Naukri',
      isProcessing: false,
    });
    const rawArtifact = latestArtifacts?.[card.key] || null;
    const rawArtifactKind = resolveArtifactKind(rawArtifact, '');
    const explicitSuccessShouldWin =
      !!(
        latestRunExplicitSuccessEntry &&
        (!latestRunState.lastFailure || latestRunExplicitSuccessEntry.index > latestRunState.lastFailure.index) &&
        !latestRunState.windowEntries.some(
          (entry) => entry.index > latestRunExplicitSuccessEntry.index && !isSuccessContextOnlyEntry(entry)
        )
      );
    const hasUnresolvedLatestRun =
      latestRunState.hasRunStarted &&
      (
        latestRunState.hasNonTerminalProgress ||
        (latestRunState.lastFailure && !latestRunState.failureShouldWin) ||
        (latestRunState.lastSuccess && !latestRunState.successShouldWin)
      ) &&
      !latestRunState.terminalShouldWin;
    // backendChannelStatus already declared above (used for dbExplicitFailed)
    const hasActiveRunNow =
      card.processing ||
      persistedStatus === 'processing' ||
      backendChannelStatus === 'processing' ||
      perChannelDbStatus === 'processing' ||
      hasUnresolvedLatestRun;
    const completedStepSummaryShouldWin =
      rawStepProgress?.tone === 'success' &&
      rawStepProgress?.currentStepNumber === rawStepProgress?.totalSteps;
    // DB failure status always wins over stale 'posted' data from a previous successful run
    if (!dbExplicitFailed && persistedStatus === 'posted') return 'success';
    // Do not let log-analysis override an explicit DB failure
    if (!dbExplicitFailed && latestRunState.latestTerminalIsSuccess && latestRunState.successShouldWin) return 'success';
    if (!dbExplicitFailed && latestRunExplicitSuccess && explicitSuccessShouldWin) return 'success';
    if (!dbExplicitFailed && completedStepSummaryShouldWin) return 'success';
    if (!dbExplicitFailed && rawArtifactKind === 'success' && !latestRunState.failureShouldWin) return 'success';
    if (hasActiveRunNow && !latestRunState.failureShouldWin && !latestRunState.latestTerminalIsSuccess) {
      return 'processing';
    }
    if (persistedStatus === 'failed') return 'failed';
    if (latestRunState.lastFailure && latestRunState.failureShouldWin) return 'failed';
    if (persistedStatus === 'processing') return 'processing';
    if (backendChannelStatus === 'posted') return 'success';
    if (backendChannelStatus === 'failed') return 'failed';
    if (backendChannelStatus === 'processing') return 'processing';
    // Per-channel DB status from hr_talent_gpt_process_tracking
    if (perChannelDbStatus === 'complete') return 'success';
    if (perChannelDbStatus === 'failed') return 'failed';
    if (perChannelDbStatus === 'processing') return 'processing';
    if (card.processing) return 'processing';
    if (hasUnresolvedLatestRun) {
      if (latestRunMaintenanceOnly && hasAnyExplicitPostingSuccess) return 'success';
      return 'processing';
    }
    if (backendChannelStatus === 'not_started') return 'not_started';
    if (normalizedPostedChannels.includes(card.key) && !latestRunState.hasRunStarted) {
      return 'success';
    }
    if (card.logs.length === 0) return 'not_started';
    return 'idle';
  };
  const formatRelativeTime = (dateValue) => {
    if (!dateValue) return null;

    const syncDate = dateValue instanceof Date ? dateValue : new Date(dateValue);
    if (Number.isNaN(syncDate.getTime())) return null;

    const diffSeconds = Math.max(0, Math.floor((relativeTimeNow - syncDate.getTime()) / 1000));
    if (diffSeconds < 60) return 'just now';
    if (diffSeconds < 3600) return `${Math.floor(diffSeconds / 60)}m ago`;
    if (diffSeconds < 86400) return `${Math.floor(diffSeconds / 3600)}h ago`;
    return `${Math.floor(diffSeconds / 86400)}d ago`;
  };

  const totalUpdates = processCards.reduce((sum, card) => sum + card.logs.length, 0);
  const processSummaryCards = processCards.map((card) => {
    const backendChannelResult = backendChannelResults?.[card.key] || null;
    const persistedStatus = String(backendChannelResult?.status || '').toLowerCase();
    const persistedStartedAt = backendChannelResult?.started_at || null;
    const persistedCompletedAt = backendChannelResult?.completed_at || null;
    const rawArtifact = latestArtifacts?.[card.key] || null;
    const channelStatus = getChannelStatus(card);
    const explicitSuccess = hasLatestRunExplicitPostingSuccess(card.logs);
    const provisionalProcessing = channelStatus === 'processing' && !explicitSuccess;
    const displayLogs =
      channelStatus === 'failed'
        ? getFailedRunLogs(card.logs)
        : channelStatus === 'success' && explicitSuccess && !provisionalProcessing
        ? getLogsThroughLastExplicitSuccess(card.logs)
        : card.logs;
    const insight = getProcessInsight(displayLogs, provisionalProcessing, {
      completedLabel: 'Posted',
      idleLabel: normalizedAvailableChannels.includes(card.key) ? 'Ready' : 'Queued',
      emptySummary: 'This channel has not started yet.',
    });
    const latestRunState = getLatestRunState(displayLogs);
    const hasStarted = latestRunState.hasRunStarted || displayLogs.length > 0;
    const stepProgress = getJobPostingStepProgressSummary({
      logs: displayLogs,
      platform: card.key === 'linkedin' ? 'LinkedIn' : 'Naukri',
      isProcessing: provisionalProcessing,
    });
    const visibleStatus =
      channelStatus === 'success'
        ? 'success'
        : channelStatus === 'failed'
        ? 'failed'
        : provisionalProcessing || insight.tone === 'active'
        ? 'processing'
        : insight.tone === 'danger'
        ? 'failed'
        : insight.tone === 'success'
        ? 'success'
        : channelStatus;
    const resolvedStepProgress =
      visibleStatus === 'success'
        ? {
            ...stepProgress,
            tone: 'success',
            statusLabel: 'Completed',
            currentStepNumber: stepProgress.totalSteps,
            stepCountLabel: `${stepProgress.totalSteps}/${stepProgress.totalSteps} steps`,
            stepName: 'Complete',
          }
        : visibleStatus === 'failed'
        ? {
            ...stepProgress,
            tone: 'failed',
            statusLabel: 'Failed',
          }
        : visibleStatus === 'processing'
        ? {
            ...stepProgress,
            tone: 'processing',
            statusLabel: 'Processing',
          }
        : stepProgress;
    const effectiveProcessing = visibleStatus === 'processing';
    const finalStepProgress =
      visibleStatus === 'failed' && persistedStatus !== 'posted'
        ? {
            ...resolvedStepProgress,
            tone: 'failed',
            statusLabel: 'Failed',
          }
        : resolvedStepProgress;

    const trackerFailureReason = getChannelTrackerFailureReason(card.key, channelProcessStatus);
    const detailedLogFailureReason = extractDetailedFailureFromLogs(displayLogs);
    const persistedFailureReason =
      detailedLogFailureReason ||
      rawArtifact?.failureMessage ||
      backendChannelResult?.failure_reason ||
      backendChannelResult?.error_details ||
      backendChannelResult?.error ||
      backendChannelResult?.message ||
      insight.summary ||
      card.metric;
    const preferredFailureReason =
      trackerFailureReason &&
      (
        !persistedFailureReason ||
        isGenericJobPostingErrorMessage(persistedFailureReason) ||
        String(trackerFailureReason).trim().length > String(persistedFailureReason || '').trim().length
      )
        ? trackerFailureReason
        : persistedFailureReason;

    const resolvedSummary =
      visibleStatus === 'success'
        ? backendChannelResult?.message || rawArtifact?.successMessage || insight.summary || card.metric
        : visibleStatus === 'failed'
        ? formatJobPostingErrorMessage(preferredFailureReason, {
            platform: card.key === 'linkedin' ? 'LinkedIn' : 'Naukri',
            detail: trackerFailureReason,
          })
        : insight.summary || card.metric;

    const hasRetryContext =
      normalizedAvailableChannels.includes(card.key) ||
      selectedChannelKeys.includes(card.key) ||
      latestRunState.hasRunStarted ||
      card.logs.length > 0;
    const canRetryChannel =
      visibleStatus === 'failed'
        ? hasRetryContext
        : !normalizedPostedChannels.includes(card.key) && hasRetryContext;

    return {
      ...card,
      channelStatus,
      visibleStatus,
      effectiveProcessing,
      displayLogs,
      insight,
      failureReason: preferredFailureReason,
      stepProgress: finalStepProgress,
      summary: resolvedSummary,
      statusLabel:
        visibleStatus === 'success'
          ? 'Posted'
          : visibleStatus === 'failed'
          ? 'Failed'
          : insight.label,
      timeLabel: effectiveProcessing && latestRunState.lastUpdatedAt
        ? `Updated ${formatRelativeTime(latestRunState.lastUpdatedAt) || 'just now'}`
        : !effectiveProcessing && persistedCompletedAt
        ? `Completed ${formatRelativeTime(persistedCompletedAt) || 'just now'}`
        : !effectiveProcessing && persistedStartedAt
        ? `Started ${formatRelativeTime(persistedStartedAt) || 'just now'}`
        : !effectiveProcessing && latestRunState.completedAt
        ? `Completed ${formatRelativeTime(latestRunState.completedAt) || 'just now'}`
        : hasStarted && latestRunState.startedAt
        ? `Started ${formatRelativeTime(latestRunState.startedAt) || 'just now'}`
        : 'Not started',
      actionLabel:
        visibleStatus === 'failed' && canRetryChannel
          ? 'Repost'
          : visibleStatus === 'not_started' && normalizedAvailableChannels.includes(card.key) && !normalizedPostedChannels.includes(card.key)
          ? 'Post job'
          : '',
    };
  });

  const hasVisibleProcessing = processSummaryCards.some((card) => card.visibleStatus === 'processing');
  const hasVisibleFailure = processSummaryCards.some((card) => card.visibleStatus === 'failed');
  const showWaitNotice = hasVisibleProcessing || linkedinProcessing || naukriProcessing || jobPostingActive;
  const summaryCardByKey = processSummaryCards.reduce((acc, card) => {
    acc[card.key] = card;
    return acc;
  }, {});
  const activityLabel = hasVisibleProcessing
    ? 'Processing'
    : hasVisibleFailure
    ? 'Failed'
    : totalUpdates > 0
    ? 'Latest activity'
    : 'Queued';

  return (
    <div className="chat_spacing_sm">
      <div
        className="chat_mb_sm"
        style={{
          borderRadius: 16,
          border: `1px solid ${isDark ? 'rgba(71, 85, 105, 0.5)' : 'rgba(177, 196, 232, 0.9)'}`,
          background: isDark
            ? 'rgba(15, 23, 42, 0.82)'
            : 'linear-gradient(135deg, rgba(255,255,255,0.99) 0%, rgba(241,248,255,0.98) 100%)',
          boxShadow: isDark ? 'none' : '0 10px 24px rgba(59, 91, 219, 0.08)',
          padding: '10px 12px',
        }}
      >
        <div
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            textAlign: 'left',
            flexWrap: 'wrap',
          }}
        >
          <div style={{ minWidth: 0 }}>
            <h5 className="chat_section_heading" style={{ margin: 0 }}>
              Posting Activity
            </h5>
            <p style={{ fontSize: 12, color: isDark ? '#94A3B8' : '#64748B', margin: '4px 0 0' }}>
              {activityLabel} · {totalUpdates} update{totalUpdates === 1 ? '' : 's'}
            </p>
          </div>
        </div>

        {processSummaryCards.length > 0 && (
          <div
            className="mt-3"
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr',
              gap: 10,
            }}
          >
            {processSummaryCards.map((card) => (
              <ProcessStatusCard
                key={card.key}
                title={card.title}
                icon={<PlatformIcon label={card.key === 'linkedin' ? 'LinkedIn' : 'Naukri'} />}
                metric={card.metric}
                logs={card.displayLogs}
                processing={card.effectiveProcessing}
                completedLabel="Posted"
                idleLabel={normalizedAvailableChannels.includes(card.key) ? 'Ready' : 'Queued'}
                emptySummary="This channel has not started yet."
                isDark={isDark}
                summaryOverride={card.visibleStatus === 'failed' ? card.summary : null}
                reasonOverride={card.visibleStatus === 'failed' ? (card.failureReason || card.summary) : null}
                statusOverride={
                  card.visibleStatus === 'success'
                    ? 'success'
                    : card.visibleStatus === 'failed'
                    ? 'failed'
                    : card.visibleStatus === 'processing'
                    ? 'processing'
                    : null
                }
                supplementaryContent={card.stepProgress?.hasLogs ? (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      flexWrap: 'wrap',
                    }}
                  >
                    <span
                      style={{
                        fontSize: 12,
                        fontWeight: 800,
                        color: isDark ? '#E2E8F0' : '#0F172A',
                      }}
                    >
                      {card.stepProgress.heading}
                    </span>
                    <span
                      style={{
                        padding: '4px 8px',
                        borderRadius: 999,
                        background: card.stepProgress.tone === 'failed'
                          ? (isDark ? 'rgba(127, 29, 29, 0.3)' : 'rgba(254, 226, 226, 0.95)')
                          : card.stepProgress.tone === 'success'
                          ? (isDark ? 'rgba(20, 83, 45, 0.3)' : 'rgba(220, 252, 231, 0.95)')
                          : (isDark ? 'rgba(30, 64, 175, 0.28)' : 'rgba(239, 246, 255, 0.96)'),
                        color: card.stepProgress.tone === 'failed'
                          ? (isDark ? '#FECACA' : '#B91C1C')
                          : card.stepProgress.tone === 'success'
                          ? (isDark ? '#BBF7D0' : '#15803D')
                          : (isDark ? '#BFDBFE' : '#1D4ED8'),
                        fontSize: 11,
                        fontWeight: 800,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {card.stepProgress.tone === 'processing' ? '● ' : ''}
                      {card.stepProgress.statusLabel}
                    </span>
                    <span
                      style={{
                        fontSize: 12,
                        fontWeight: 700,
                        color: isDark ? '#CBD5E1' : '#475569',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {card.stepProgress.stepCountLabel}
                    </span>
                    {card.stepProgress.stepName && (
                      <span
                        style={{
                          fontSize: 12,
                          color: isDark ? '#94A3B8' : '#64748B',
                          overflowWrap: 'anywhere',
                        }}
                      >
                        {card.stepProgress.tone === 'failed' ? `Stopped at ${card.stepProgress.stepName}` : card.stepProgress.stepName}
                      </span>
                    )}
                  </div>
                ) : null}
                actionLabel={card.actionLabel}
                onAction={onRetryChannel && card.actionLabel
                  ? () => onRetryChannel(card.key)
                  : undefined}
                onViewLogs={() => setActiveLogModal(card.key)}
                allowEmptyLogView={card.visibleStatus === 'processing' || card.logs.length > 0}
              />
            ))}
          </div>
        )}
        {showWaitNotice && <JobPostingWaitNotice isDark={isDark} />}
        {processCards.length === 0 && (
          <div
            style={{
              marginTop: 12,
              borderRadius: 14,
              border: `1px solid ${isDark ? 'rgba(71,85,105,0.55)' : 'rgba(203,213,225,0.9)'}`,
              background: isDark ? 'rgba(15, 23, 42, 0.72)' : 'rgba(255,255,255,0.9)',
              padding: 14,
            }}
          >
            {postedChannelLabels.length > 0 ? (
              <>
                <div style={{ fontSize: 15, fontWeight: 800, color: isDark ? '#F8FAFC' : '#0F172A' }}>Job posted successfully</div>
                <div style={{ fontSize: 12, color: isDark ? '#CBD5E1' : '#475569', marginTop: 4 }}>
                  Posted on {postedChannelLabels.join(', ')}. Sync applicants when you want to pull latest Naukri/LinkedIn applications.
                </div>
              </>
            ) : (
              <>
                <div style={{ fontSize: 15, fontWeight: 800, color: isDark ? '#F8FAFC' : '#0F172A' }}>Job posting not initialized</div>
                <div style={{ fontSize: 12, color: isDark ? '#CBD5E1' : '#475569', marginTop: 4 }}>
                  Start the job-posting agent from chat. It will guide platform selection and required fields.
                </div>
                {onRequestPostJob && (
                  <button
                    type="button"
                    onClick={onRequestPostJob}
                    style={{
                      marginTop: 12,
                      border: 'none',
                      borderRadius: 999,
                      padding: '9px 14px',
                      background: 'linear-gradient(135deg, #3b5bdb 0%, #28aca8 100%)',
                      color: '#fff',
                      fontSize: 12,
                      fontWeight: 800,
                      cursor: 'pointer',
                    }}
                  >
                    Start Post Job Agent
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {(['linkedin', 'naukri'].some((key) => {
        const artifact = latestArtifacts[key];
        const summaryCard = processSummaryCards.find((card) => card.key === key);
        if (!summaryCard) return false;
        if (!artifact) return summaryCard.channelStatus === 'failed';
        const artifactKind = resolveArtifactKind(artifact, summaryCard.channelStatus);
        if (artifactKind === 'failure') return summaryCard.channelStatus === 'failed';
        if (artifactKind === 'success') return summaryCard.channelStatus === 'success';
        return false;
      })) && (
        <div className="chat_mb_lg">
          <div className="flex items-center justify-between chat_mb_md">
            <h5 className="chat_section_heading" style={{ margin: 0 }}>
              Screenshots
            </h5>
            <span
              className="chat_font-medium"
              style={{
                fontSize: '13px',
                color: isDark ? '#CBD5E1' : '#475569',
                lineHeight: '20px',
              }}
            >
              Screenshot, URL, and page title from the latest run
            </span>
          </div>
          <div
            className={`grid gap-4 ${(latestArtifacts.linkedin && latestArtifacts.naukri) ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1'}`}
            style={{ minWidth: 0 }}
          >
            {['linkedin', 'naukri'].map((key) => {
              const artifact = latestArtifacts[key];
              const summaryCard = processSummaryCards.find((card) => card.key === key);
              if (!summaryCard) return null;
              if (!artifact && summaryCard.channelStatus !== 'failed') return null;
              if (!artifact) {
                return (
                  <ChatCard
                    key={key}
                    variant="secondary"
                    size="lg"
                    rounded="xl"
                    className="chat_no_shadow"
                    style={{
                      backgroundColor: isDark ? 'rgba(15, 23, 42, 0.72)' : 'rgba(255, 255, 255, 0.94)',
                      borderColor: isDark ? 'rgba(239, 68, 68, 0.22)' : 'rgba(252, 165, 165, 0.65)',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
                      <div style={{ fontSize: 16, fontWeight: 700, color: isDark ? '#F8FAFC' : '#0F172A' }}>
                        {key === 'linkedin' ? 'LinkedIn failure evidence' : 'Naukri failure evidence'}
                      </div>
                      <span style={{
                        fontSize: 11,
                        fontWeight: 700,
                        color: '#DC2626',
                        background: isDark ? 'rgba(127,29,29,0.28)' : 'rgba(254,242,242,0.92)',
                        padding: '6px 10px',
                        borderRadius: 999,
                      }}>
                        No screenshot captured
                      </span>
                    </div>
                    <div style={{ fontSize: 13, lineHeight: 1.6, color: isDark ? '#E2E8F0' : '#334155' }}>
                      The latest failed run did not emit an error screenshot. The failure summary is still available in the posting log.
                    </div>
                    {summaryCard?.insight?.reason && (
                      <div style={{ marginTop: 12, fontSize: 12, color: isDark ? '#CBD5E1' : '#475569' }}>
                        <strong>Reason:</strong> {summaryCard.insight.reason}
                      </div>
                    )}
                  </ChatCard>
                );
              }
              const artifactKind = resolveArtifactKind(artifact, summaryCard.channelStatus);
              if (artifactKind === 'failure' && summaryCard.channelStatus !== 'failed') return null;
              if (artifactKind === 'success' && summaryCard.channelStatus !== 'success') return null;
              if (artifactKind === 'neutral') return null;

              const resolvedArtifact = { ...artifact, kind: artifactKind };
              const isFailureArtifact = artifactKind === 'failure';

              return (
                <ChatCard
                  key={key}
                  variant="secondary"
                  size="lg"
                  rounded="xl"
                  className="chat_no_shadow"
                  style={{
                    minWidth: 0,
                    overflow: 'hidden',
                    backgroundColor: isDark ? 'rgba(15, 23, 42, 0.72)' : 'rgba(255, 255, 255, 0.94)',
                    borderColor: isFailureArtifact
                      ? (isDark ? 'rgba(239, 68, 68, 0.22)' : 'rgba(252, 165, 165, 0.65)')
                      : (isDark ? 'rgba(34, 197, 94, 0.22)' : 'rgba(134, 239, 172, 0.72)'),
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
                    <div style={{ fontSize: 16, fontWeight: 700, color: isDark ? '#F8FAFC' : '#0F172A' }}>
                      {key === 'linkedin' ? 'LinkedIn posting screenshot' : 'Naukri posting screenshot'}
                    </div>
                    <span style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: isFailureArtifact ? '#DC2626' : '#15803D',
                      background: isFailureArtifact
                        ? (isDark ? 'rgba(127,29,29,0.28)' : 'rgba(254,242,242,0.92)')
                        : (isDark ? 'rgba(20,83,45,0.32)' : 'rgba(220,252,231,0.95)'),
                      padding: '6px 10px',
                      borderRadius: 999,
                    }}>
                      {isFailureArtifact ? 'Failure captured' : 'Success captured'}
                    </span>
                  </div>

                  <button
                    type="button"
                    onClick={() => setActiveImagePreview(resolvedArtifact)}
                    style={{
                      width: '100%',
                      border: 'none',
                      padding: 0,
                      background: 'transparent',
                      cursor: 'pointer',
                      textAlign: 'left',
                    }}
                  >
                    <ScreenshotImage artifact={artifact} alt={artifact.filename} isDark={isDark} />
                  </button>

                  <div style={{ marginTop: 12, fontSize: 13, lineHeight: 1.6, color: isDark ? '#E2E8F0' : '#334155' }}>
                    {isFailureArtifact
                      ? (artifact.failureMessage || 'Posting failed before the platform could complete the final submission.')
                      : (artifact.successMessage || 'Posting completed successfully.')}
                  </div>

                  <div style={{ marginTop: 12, display: 'grid', gap: 8 }}>
                    {artifact.pageUrl && (
                      <div style={{ fontSize: 12, color: isDark ? '#CBD5E1' : '#475569', minWidth: 0, overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                        <strong>Page URL:</strong> {artifact.pageUrl}
                      </div>
                    )}
                    {artifact.pageTitle && (
                      <div style={{ fontSize: 12, color: isDark ? '#CBD5E1' : '#475569', minWidth: 0, overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                        <strong>Page title:</strong> {artifact.pageTitle}
                      </div>
                    )}
                    <div style={{ fontSize: 12, color: isDark ? '#CBD5E1' : '#475569', minWidth: 0, overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                      <strong>Screenshot:</strong> {artifact.filename}
                    </div>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}>
                    <button
                      type="button"
                      onClick={() => setActiveImagePreview(resolvedArtifact)}
                      style={{
                        border: 'none',
                        borderRadius: 999,
                        padding: '8px 14px',
                        background: 'linear-gradient(135deg, #3b5bdb 0%, #28aca8 100%)',
                        color: '#fff',
                        fontSize: 12,
                        fontWeight: 700,
                        cursor: 'pointer',
                      }}
                    >
                      View screenshot
                    </button>
                  </div>
                </ChatCard>
              );
            })}
          </div>
        </div>
      )}

      <ProcessLogsModal
        open={activeLogModal === 'linkedin'}
        title="LinkedIn Posting Log"
        logs={
          fileLogsState.linkedin.fetched && fileLogsToEntries(fileLogsState.linkedin.logs).length > 0
            ? fileLogsToEntries(fileLogsState.linkedin.logs)
            : (summaryCardByKey.linkedin?.displayLogs || linkedinLogs)
        }
        isDark={isDark}
        onClose={() => setActiveLogModal(null)}
      >
        <JobPostingStepsLog
          logs={
            fileLogsState.linkedin.fetched && fileLogsToEntries(fileLogsState.linkedin.logs).length > 0
              ? fileLogsToEntries(fileLogsState.linkedin.logs)
              : (summaryCardByKey.linkedin?.displayLogs || linkedinLogs)
          }
          platform="LinkedIn"
          isProcessing={summaryCardByKey.linkedin?.effectiveProcessing ?? linkedinProcessing}
          embedded
        />
        <FullFileLogSection
          fileState={fileLogsState.linkedin}
          isDark={isDark}
          onRefetch={() =>
            setFileLogsState((prev) => ({
              ...prev,
              linkedin: { loading: false, logs: [], error: null, fetched: false },
            }))
          }
        />
      </ProcessLogsModal>

      <ProcessLogsModal
        open={activeLogModal === 'naukri'}
        title="Naukri Posting Log"
        logs={
          fileLogsState.naukri.fetched && fileLogsToEntries(fileLogsState.naukri.logs).length > 0
            ? fileLogsToEntries(fileLogsState.naukri.logs)
            : (summaryCardByKey.naukri?.displayLogs || naukriLogs)
        }
        isDark={isDark}
        onClose={() => setActiveLogModal(null)}
      >
        <JobPostingStepsLog
          logs={
            fileLogsState.naukri.fetched && fileLogsToEntries(fileLogsState.naukri.logs).length > 0
              ? fileLogsToEntries(fileLogsState.naukri.logs)
              : (summaryCardByKey.naukri?.displayLogs || naukriLogs)
          }
          platform="Naukri"
          isProcessing={summaryCardByKey.naukri?.effectiveProcessing ?? naukriProcessing}
          embedded
        />
        <FullFileLogSection
          fileState={fileLogsState.naukri}
          isDark={isDark}
          onRefetch={() =>
            setFileLogsState((prev) => ({
              ...prev,
              naukri: { loading: false, logs: [], error: null, fetched: false },
            }))
          }
        />
      </ProcessLogsModal>

      {activeImagePreview && (
        <div
          onClick={() => setActiveImagePreview(null)}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1000,
            background: 'rgba(15, 23, 42, 0.66)',
            backdropFilter: 'blur(8px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 20,
          }}
        >
          <div
            onClick={(event) => event.stopPropagation()}
            style={{
              width: 'min(980px, 100%)',
              maxHeight: '90vh',
              overflow: 'auto',
              borderRadius: 24,
              background: isDark ? 'rgba(15, 23, 42, 0.98)' : 'rgba(255, 255, 255, 0.98)',
              border: `1px solid ${isDark ? 'rgba(71, 85, 105, 0.6)' : 'rgba(226, 232, 240, 0.95)'}`,
              boxShadow: '0 30px 80px rgba(15, 23, 42, 0.28)',
              padding: 20,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 16 }}>
              <div>
                <div style={{ fontSize: 18, fontWeight: 700, color: isDark ? '#F8FAFC' : '#0F172A' }}>
                  {activeImagePreview.kind === 'failure' ? 'Failure screenshot' : 'Posting screenshot'}
                </div>
                <div style={{ fontSize: 12, color: isDark ? '#94A3B8' : '#64748B', marginTop: 4 }}>{activeImagePreview.filename}</div>
              </div>
              <button
                type="button"
                onClick={() => setActiveImagePreview(null)}
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 12,
                  border: `1px solid ${isDark ? 'rgba(71,85,105,0.8)' : 'rgba(203,213,225,1)'}`,
                  background: 'transparent',
                  color: isDark ? '#CBD5E1' : '#475569',
                  cursor: 'pointer',
                  fontSize: 18,
                }}
              >
                ×
              </button>
            </div>
            {activeImagePreviewUrl ? (
              <img
                src={activeImagePreviewUrl}
                alt={activeImagePreview.filename}
                onError={() => {
                  const imageUrls = activeImagePreview?.imageUrls || [];
                  const currentIndex = imageUrls.indexOf(activeImagePreviewUrl);
                  const nextUrl = currentIndex >= 0 ? imageUrls[currentIndex + 1] : null;
                  setActiveImagePreviewUrl(nextUrl || '');
                }}
                style={{
                  width: '100%',
                  borderRadius: 18,
                  border: `1px solid ${isDark ? 'rgba(71,85,105,0.6)' : 'rgba(226,232,240,0.95)'}`,
                }}
              />
            ) : (
              <div
                style={{
                  padding: '24px 16px',
                  borderRadius: 18,
                  border: `1px solid ${isDark ? 'rgba(71,85,105,0.6)' : 'rgba(226,232,240,0.95)'}`,
                  background: isDark ? 'rgba(15, 23, 42, 0.72)' : 'rgba(248, 250, 252, 0.92)',
                  color: isDark ? '#CBD5E1' : '#475569',
                }}
              >
                Screenshot preview is not available from the current server path.
              </div>
            )}
            <div style={{ marginTop: 16, display: 'grid', gap: 8, color: isDark ? '#E2E8F0' : '#334155', fontSize: 13, minWidth: 0 }}>
              {activeImagePreview.failureMessage && <div style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}><strong>Error:</strong> {activeImagePreview.failureMessage}</div>}
              {!activeImagePreview.failureMessage && activeImagePreview.successMessage && <div style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}><strong>Status:</strong> {activeImagePreview.successMessage}</div>}
              {activeImagePreview.imageUrl && <div style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}><strong>Screenshot path:</strong> {activeImagePreview.imageUrl}</div>}
              {activeImagePreview.pageUrl && <div style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}><strong>Page URL:</strong> {activeImagePreview.pageUrl}</div>}
              {activeImagePreview.pageTitle && <div style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}><strong>Page title:</strong> {activeImagePreview.pageTitle}</div>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobPostingLogs;
